import React, { useEffect } from 'react';

import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { User, Mail } from 'lucide-react';

import {
  useAdminProfileUpdateMutation,
  useLazyIdentityQuery,
} from './editProfileApi';

import type { FormValues, ProfileUpdateRequest } from './model';
import type { SubmitHandler } from 'react-hook-form';
import { updateUserLocally } from '../../user/profileSlice';

const EditProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [fetchIdentity, { isFetching }] = useLazyIdentityQuery();
  const [updateProfile, { isLoading: isUpdating }] =
    useAdminProfileUpdateMutation();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onSubmit', reValidateMode: 'onChange' });

  const nameValue = watch('name', '');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchIdentity().unwrap();
        const data = res.data;
        if (data) {
          setValue('name', data.name ?? '');
          setValue('email', data.email ?? '');
        }
      } catch (err: any) {
        // Error is handled globally in baseQuery
      }
    };
    fetchData();
  }, [fetchIdentity, setValue]);

  const handleCancel = () => {
    reset();
    navigate('/dashboard');
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const payload: ProfileUpdateRequest = {
      fullName: data.name,
      email: data.email,
    };
    try {
      await updateProfile(payload).unwrap();
      dispatch(updateUserLocally({ name: data.name }));
      navigate('/dashboard');
    } catch (err: any) {
      // Error is handled globally in baseQuery
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Single Outer Card */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-sm max-w-xl">
        {/* Icon Header */}
        <div className="flex items-start gap-3 p-5 border-b border-neutral-200 dark:border-neutral-800">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-brand-100 dark:bg-brand-900/40 text-brand-500">
            <User className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">
              Profile Information
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Update your account information. Your email address cannot be
              changed for security reasons.
            </p>
          </div>
        </div>

        {/* Form Fields */}
        {isFetching ? (
          <div className="p-6 text-sm text-neutral-500 dark:text-neutral-400 text-center">
            Loading profile...
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-5 space-y-4">
              {/* Name Field with User Icon + Char Count */}
              <div>
                <label
                  htmlFor="name"
                  className="text-xs font-medium text-neutral-700 dark:text-neutral-300 block mb-1.5"
                >
                  Name <span className="text-error-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500 pointer-events-none" />
                  <input
                    id="name"
                    type="text"
                    placeholder="Name"
                    className="w-full h-10 pl-9 pr-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm text-neutral-800 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition"
                    {...register('name', {
                      required: 'Name is required',
                    })}
                  />
                </div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                  {(nameValue ?? '').length}/100 characters
                </p>
                {errors.name && (
                  <p className="text-xs text-error-500 mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Email Field with Mail Icon + Security Note */}
              <div>
                <label
                  htmlFor="email"
                  className="text-xs font-medium text-neutral-700 dark:text-neutral-300 block mb-1.5"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500 pointer-events-none" />
                  <input
                    id="email"
                    type="email"
                    placeholder="Email"
                    disabled
                    className="w-full h-10 pl-9 pr-3 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg text-sm text-neutral-500 dark:text-neutral-400 cursor-not-allowed"
                    {...register('email')}
                  />
                </div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                  Your email address cannot be changed for security reasons
                </p>
              </div>
            </div>

            {/* Divider before Save/Cancel */}
            <div className="flex items-center gap-2 px-5 py-4 border-t border-neutral-200 dark:border-neutral-800">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-colors border border-neutral-200 dark:border-neutral-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdating}
                className="px-5 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Note Card Below */}
      <div className="max-w-xl bg-gray-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl px-4 py-3 shadow-sm">
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          <span className="font-semibold text-neutral-700 dark:text-neutral-300">
            Note:
          </span>{' '}
          updating your profile will not log you out from any of your active
          sessions. If you need to change your email address,{' '}
          <span className="text-brand-500">
            please contact your system administrator
          </span>
          .
        </p>
      </div>
    </div>
  );
};

export default EditProfilePage;
